'use client';

/**
 * useGatewayScript Hook
 *
 * Simple hook for loading external JavaScript SDKs (e.g., Checkout.com Frames/Flow).
 * Returns { isReady, error } - use isReady in a useEffect to initialize when the script loads.
 *
 * Usage:
 *   const { isReady, error } = useGatewayScript({
 *     globalVarName: 'Frames',
 *     scriptSrc: 'https://...',
 *   });
 *
 *   useEffect(() => {
 *     if (isReady) {
 *       // Initialize the SDK
 *     }
 *   }, [isReady]);
 */

import { useEffect, useState } from 'react';
import { asyncLoadScript } from '../../utils/asyncLoadScript';

interface UseGatewayScriptOptions {
  globalVarName: string;
  scriptSrc?: string;
  checkFunction?: () => boolean;
}

const POLL_INTERVAL = 50;
const POLL_TIMEOUT = 10000;

const isScriptReady = (globalVarName: string, checkFunction?: () => boolean): boolean => {
  if (typeof window === 'undefined') return false;
  return checkFunction ? checkFunction() : !!(window as any)[globalVarName];
};

const waitForScript = (globalVarName: string, checkFunction?: () => boolean): Promise<void> => new Promise((resolve, reject) => {
  if (isScriptReady(globalVarName, checkFunction)) {
    resolve();
    return;
  }

  const startTime = Date.now();
  const checkInterval = setInterval(() => {
    if (isScriptReady(globalVarName, checkFunction)) {
      clearInterval(checkInterval);
      resolve();
    } else if (Date.now() - startTime >= POLL_TIMEOUT) {
      clearInterval(checkInterval);
      reject(new Error(`Script ${ globalVarName } not available after ${ POLL_TIMEOUT }ms`));
    }
  }, POLL_INTERVAL);
});

export const useGatewayScript = ({
  globalVarName,
  scriptSrc,
  checkFunction
}: UseGatewayScriptOptions) => {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    // Already loaded
    if (isScriptReady(globalVarName, checkFunction)) {
      setIsReady(true);
      setError(null);
      return undefined;
    }

    // No script source provided yet — stay in "waiting" state (no error).
    // Config may arrive later, at which point scriptSrc will be set and the
    // effect will re-run.
    if (!scriptSrc) {
      return undefined;
    }

    let cancelled = false;

    asyncLoadScript(scriptSrc, (window as any)[globalVarName])
      .then(() => !cancelled && waitForScript(globalVarName, checkFunction))
      .then(() => {
        if (!cancelled) {
          setIsReady(true);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Failed to load gateway script'));
          setIsReady(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [scriptSrc, globalVarName, checkFunction]);

  return { isReady, error };
};
